import { Loader } from 'components/common/Loader';
import { userRoles } from 'constants/constants';
import DocumentsUpload from 'pages/Projects/DocumentsUpload';
import ProjectChecklist from 'pages/Projects/ProjectChecklist';
import Subtask from 'pages/Projects/Subtask/Subtask';
import Teams from 'pages/Projects/Teams';
import { useEffect } from 'react';
import { Card } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  projectUserRole,
  setHeaderTitle,
  showDropdown,
} from 'redux/projectSlice';
import {
  useAddProjectMutation,
  useGetProjectByIdMutation,
} from 'redux/services/project';

import './EditProjectDetails.scss';

export type EditProjectDetailsProps = {
  section: 'teams' | 'subProjects' | 'checklist' | 'documents';
};

const EditProjectDetails: React.FC<EditProjectDetailsProps> = ({ section }) => {
  const { setupId: projectId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [getProjectById, { data: project, isLoading }] =
    useGetProjectByIdMutation();
  const [addProject, { isLoading: addOpsLoading }] = useAddProjectMutation();
  const navigate = useNavigate();

  const fetchProjectDetails = () => {
    getProjectById(projectId);
  };

  useEffect(() => {
    if (projectId) {
      getProjectById(projectId);
    }
  }, [getProjectById, projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(showDropdown(true));
      if (project) {
        dispatch(projectUserRole(project.role));
        dispatch(
          setHeaderTitle({
            title: project.projectInfo.projectName,
            subTitle: project.projectInfo.projectId,
          }),
        );
      }
    } else {
      dispatch(showDropdown(false));
      dispatch(setHeaderTitle(null));
    }

    return () => {
      dispatch(showDropdown(false));
    };
  }, [project, dispatch, projectId]);

  const handleUploadDoc = () => fetchProjectDetails();

  const handleSubmit = async (params: any) => {
    const projectValues = {
      ...params.values,
    };
    const addBody = {
      values: projectId ? { ...project, ...projectValues } : projectValues,
    };
    const res: any = await addProject(addBody);
    if (res.data) {
      toast.success(t('save_success'));
      navigate(-1);
    } else {
      toast.error(res.error.data.message);
      return false;
    }
  };

  const renderPageTitle = () => {
    switch (section) {
      case 'teams':
        return t('label_define_your_team');
      case 'checklist':
        return t('label_select_checklist');
      case 'documents':
        return t('btn_upload_documents');
      default:
        return t('label_create_sub_projects');
    }
  };

  const renderPage = () => {
    const disabled =
      project?.role === userRoles.DOER || project?.role === userRoles.INSPECTOR;
    switch (section) {
      case 'teams':
        return <Teams disabled={disabled} isSingleEdit />;
      case 'checklist':
        return (
          <ProjectChecklist
            isSingleEdit
            disabled={disabled}
            onSubmitProject={handleSubmit}
            projectDetails={project?.checklistLibraryId ?? ''}
          />
        );
      case 'documents':
        return (
          <DocumentsUpload
            isSingleEdit
            projectDetails={project?.documents ?? null}
            isLaunched={project?.isLaunched}
            setRefresh={() => {}}
            refresh={false}
            disabled={disabled}
            onUploadDoc={handleUploadDoc}
          />
        );

      default:
        return (
          <Subtask
            isSingleEdit
            disabled={disabled}
            onSubmitProject={handleSubmit}
            projectDetails={project?.tasks ?? []}
          />
        );
    }
  };

  return (
    <div className="edit-project-details">
      {isLoading || addOpsLoading ? (
        <Loader />
      ) : (
        <>
          <h4 className="page-title">{renderPageTitle()}</h4>
          <Card className="project-setup-steps border-0 flex-grow-1 overflow-auto">
            <Card.Body className="h-100 d-flex flex-column">
              {!!project && renderPage()}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default EditProjectDetails;
